/**
 * <StudentHomeSkeleton> — la forme de l'accueil de l'élève pendant son premier chargement.
 *
 * Le cas courant : un élève qui a un prochain live, une mission de Sam en cours et un jeu, un jour
 * sans live (cinq jours sur sept pour un élève à une séance par semaine). Le salut (le prénom est
 * connu avant la page : il s'écrit), l'anneau et la ligne des compteurs ; le bloc du live dans son
 * cadre, sous son vrai intitulé, sa capsule en grand et sans bouton ; la carte de la mission
 * dessous ; les deux lives suivants sous « À venir » ; la carte du prochain contrôle (elle est toujours là, avec un contrôle ou « Ajouter un
 * contrôle ») ; puis la carte de la quête et la ligne du rang. Le bandeau d'essai n'est pas deviné :
 * il arrive avec les données.
 *
 * Les formes viennent de leurs composants (`<NextSessionHeroSkeleton>`, `<SamMissionCardSkeleton>`,
 * `<SchoolAssessmentRowSkeleton>`, `<StudentGameSummarySkeleton>`), et les colonnes de
 * `<StudentHomeColumns>` : rien n'est recopié ici.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { CardList } from "../../molecules/CardList"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { List } from "../../molecules/List"
import { SamMissionCardSkeleton } from "../../molecules/SamMissionCard"
import { SchoolAssessmentRowSkeleton } from "../../molecules/SchoolAssessmentRow/SchoolAssessmentRowSkeleton"
import { NextSessionHeroSkeleton } from "../../organisms/NextSessionHero"
import { StudentGameSummarySkeleton } from "../../organisms/StudentGameSummary/StudentGameSummarySkeleton"
import { StudentHomeColumns } from "./StudentHomeColumns"
import { StudentHomeFollowingLivesSkeleton } from "./StudentHomeFollowingLives"
import { StudentHomeGreetingSkeleton } from "./StudentHomeGreeting"

export type StudentHomeSkeletonProps = {
    /** Le prénom de l'élève, s'il est déjà connu : le salut s'écrit sans attendre. */
    firstname?: string
}

export function StudentHomeSkeleton(props: StudentHomeSkeletonProps) {
    const { firstname } = props

    return (
        <Stack gap="6">
            <StudentHomeGreetingSkeleton firstname={firstname} />

            <Stack gap="4">
                <List title="Prochaine session LIVE">
                    <HighlightFrame>
                        <CardList>
                            <NextSessionHeroSkeleton />
                        </CardList>
                    </HighlightFrame>
                </List>
                <SamMissionCardSkeleton />
            </Stack>

            <StudentHomeFollowingLivesSkeleton />

            <StudentHomeColumns
                main={
                    <CardList>
                        <SchoolAssessmentRowSkeleton />
                    </CardList>
                }
                game={<StudentGameSummarySkeleton />}
            />
        </Stack>
    )
}
